/**
 * One of Penelope's two side switches (PEN-01): "may she BUY this team?". Rendered beside the team's own book on
 * Game Detail, in the ticket, and on the /live strip — all bound to the same state (usePenelopeSides). The order
 * itself rests on the OTHER team's market (a NO bid), which the tooltip says plainly; the label names the team the
 * trader ends up long, which is the side Doom reads.
 */
import { cn } from '@/lib/infra/utils';
import type { PenelopeSide, PenelopeSides } from '@/hooks/usePenelopeSides';

export function PenelopeSideToggle({ sides, side, teamCode, otherCode, compact = false }: {
  sides: PenelopeSides;
  side: PenelopeSide;
  teamCode: string;
  otherCode: string;
  /** Book-side / strip: a short label; the sentence moves to the tooltip. */
  compact?: boolean;
}) {
  const { control, armed, setSide } = sides;
  if (!control.game && !control.eligible) return null; // not offered where she cannot run
  const on = armed[side];
  const game = control.game;
  const running = game?.config.enabled === true;
  const live = game?.config.mode === 'live';
  const title = on
    ? `Penelope may BUY ${teamCode}: NO bids on ${otherCode}'s market, at the best bid or not at all. Untick to stop her buying ${teamCode} — she still sells ${teamCode} back.`
    : `Tick to let Penelope buy ${teamCode}${game ? '' : ' when you run her'}: NO bids on ${otherCode}'s market, at the best bid or not at all.`;
  return (
    <label
      title={title}
      data-penelope-side={side}
      className={cn(
        'inline-flex cursor-pointer select-none items-center gap-1 whitespace-nowrap font-mono uppercase',
        compact ? 'text-[9px]' : 'text-[10px]',
        on && running ? (live ? 'text-red-500' : 'text-sky-500') : on ? 'text-foreground' : 'text-muted-foreground',
        control.busy && 'opacity-60',
      )}
    >
      <input
        type="checkbox"
        className="h-3 w-3"
        checked={on}
        disabled={control.busy}
        onChange={(e) => setSide(side, e.target.checked)}
        aria-label={`Penelope buys ${teamCode}`}
      />
      {compact ? `pen buy ${teamCode}` : `buy ${teamCode}`}
    </label>
  );
}
